<template>
  <div class="d-flex flex-column row-gap-3" data-locator="signatures-tracking">
    <div class="text--medium text--extra-bold">
      {{$t('project.project-settings.signatures.ProjectSettingsSignatures.signatures')}} 
      <span v-if="signatures">({{signatures.pagination.itemCount}})</span>
      <AppTooltip v-if="canGlobalChase">
        <template #activator="{ props }">
          <AppButton color="primary"
                     small
                     :disabled="globalChaseDisabled"
                     class="ml-2"
                     data-locator="global-chase-button"
                     :loading="globalChaseLoading"
                     v-bind="props"
                     style="z-index: 1"
                     @click="prepareGlobalChase"
          >
            {{ $t('project.project-settings.signatures.ProjectSettingsSignatures.globalChase') }}
          </AppButton>
        </template>
        <span>{{ globalChaseTooltip }}</span>
      </AppTooltip>
    </div>
    <AppCard 
      class="border"
      data-locator="signatures-tracking-table"
      elevation="0"
      >
      <template v-if="signaturesPending">
        <v-container fluid class="bg-white px-8 py-4">
          <v-skeleton-loader type="text@3"></v-skeleton-loader>
        </v-container>
      </template>
      <template v-else>
          <v-data-table v-if="signatures"
                        :custom-key-sort="customKeySort"
                        :headers="tableHeaders"
                        :items-per-page="itemsPerPage"
                        :items="signatures.data"
                        :page="currentPage"
                        class="ProjectSettingsSignatures-table"
                        show-expand
                        v-model:expanded="expanded"
          >
            <template #item.data-table-expand="{ item }">
              <AppButton v-if="showExpand(item)"
                         color="primary"
                         icon
                         size="x-small"
                         variant="text"
                         :disabled="!canExpand(item)"
                         @click="onExpandClick(item)"
              >
                <font-awesome-icon :icon="['far', 'chevron-right']"
                                   class="ProjectSettingsSignatures-expandIcon"
                                   :class="{'ProjectSettingsSignatures-expandIcon--open': expanded.includes(item.id)}"
                />
              </AppButton>
            </template>

            <template #expanded-row="{ item }">
              <tr class="expanded-row">
                <td :colspan="2" />
                <td class="pb-4">
                  <ProjectSettingsSignaturesValidatorsList :item="item" />
                </td>
                <td class="pb-4">
                  <ProjectSettingsSignaturesSignatoriesList :item="item" />
                </td>
                <td :colspan="3" />
              </tr>
            </template>
            <template #item.validators="{item}">
              <ProjectSettingsSignaturesValidatorsSummary :item="item" />
            </template>
            <template #item.signatories="{item}">
              <ProjectSettingsSignaturesSignatoriesSummary :item="item" />
            </template>
            <template #item.status="{item}">
              <v-chip :color="envelopeStatusColor(item)" label variant="flat">
                {{ $t(`common.envelopeStatus.${item.status}`) }}
              </v-chip>
            </template>
            <template #item.date="{ value }">
              <div v-if="value">
                {{ getDate(value) }}
              </div>
            </template>
            <template #item.options="{item}">
              <div class="d-flex align-center justify-end">
                <ProjectSettingsSignaturesEnvelopeChaseButton :item="item" :current-date="currentDate"/>

                <AppTooltip>
                  <template #activator="{ props }">
                    <div v-bind="props">
                      <AppDownloadFile :link="envelopeResourceLink(item.id)" forceReader>
                        <template #default="{submitForm}">
                          <AppButton icon
                                     class="text-primary"
                                     @click="submitForm"
                          >
                            <app-icon icon-name="eye" icon-weight="fas" class="text-accent-lighten-2" />
                          </AppButton>
                        </template>
                      </AppDownloadFile>
                    </div>
                  </template>
                  <span>{{ $t('project.project-settings.signatures.ProjectSettingsSignatures.previewTooltip') }}</span>
                </AppTooltip>
              </div>
            </template>

            <template #bottom>
              <div class="d-flex justify-end mt-4">
                <AppPagination v-model:current-page="currentPage"
                               v-model:page-size="itemsPerPage"
                               :total="signatures.data.length"
                />
              </div>
            </template>
          </v-data-table>
      </template>
    </AppCard>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions, mapGetters, mapState, useStore } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { usePagination } from '@/common/pagination/usePagination'
import { ISOToShortDate } from '@/common/utils/dates'
import { numericSort } from '@/common/utils/sorting'
import { toast } from '@/common/utils/toast'
import { CHASE_USERS, CHASE_VALIDATOR, GET_SIGNATURES } from '@/store/modules/room/action_types'
import { ADD_CHASE_TIMER_GLOBAL } from '@/store/modules/room/mutation_types'

import ProjectSettingsSignaturesEnvelopeChaseButton from './ProjectSettingsSignaturesEnvelopeChaseButton.vue'
import ProjectSettingsSignaturesSignatoriesList from './ProjectSettingsSignaturesSignatoriesList.vue'
import ProjectSettingsSignaturesSignatoriesSummary from './ProjectSettingsSignaturesSignatoriesSummary.vue'
import ProjectSettingsSignaturesValidatorsList from './ProjectSettingsSignaturesValidatorsList.vue'
import ProjectSettingsSignaturesValidatorsSummary from './ProjectSettingsSignaturesValidatorsSummary.vue'

export default {
  name: 'ProjectSettingsSignatures',
  components: {
    AppButton,
    AppCard,
    AppDownloadFile,
    AppPagination,
    AppTooltip,
    ProjectSettingsSignaturesEnvelopeChaseButton,
    ProjectSettingsSignaturesSignatoriesList,
    ProjectSettingsSignaturesSignatoriesSummary,
    ProjectSettingsSignaturesValidatorsList,
    ProjectSettingsSignaturesValidatorsSummary,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  setup: () => {
    const store = useStore()
    const { currentPage, itemsPerPage } = usePagination({
      currentUserId: store.getters['user/currentUserId'],
      userSettingsKey: 'project-settings-signatures-items-per-page',
    })

    return {
      currentPage,
      itemsPerPage,
    }
  },
  data () {
    return {
      expanded: [],
      tableHeaders: [
        { title: '', key: 'data-table-expand' },
        {
          title: this.$t('project.project-settings.signatures.ProjectSettingsSignatures.headers.documents'),
          key: 'documents',
          value: 'file.basename',
          sortable: true,
        },
        {
          title: this.$t('project.project-settings.signatures.ProjectSettingsSignatures.headers.validators'),
          key: 'validators',
          value: 'validations',
          sortable: true,
        },
        {
          title: this.$t('project.project-settings.signatures.ProjectSettingsSignatures.headers.signatories'),
          value: 'signatories',
          sortable: true,
        },
        {
          title: this.$t('project.project-settings.signatures.ProjectSettingsSignatures.headers.status'),
          value: 'status',
          sortable: true,
        },
        {
          title: this.$t('project.project-settings.signatures.ProjectSettingsSignatures.headers.date'),
          key: 'date',
          value: 'timestamp',
          sortable: true,
        },
        {
          title: this.$t('project.project-settings.signatures.ProjectSettingsSignatures.headers.options'),
          align: 'end',
          value: 'options',
        },
      ],
      currentDate: dayjs(),
      currentDateIntervalId: null,
      globalChaseLoading: false,
      envelopeChaseLoading: null,
    }
  },
  computed: {
    ...mapState('room', ['signatures', 'signaturesPending', 'chaseTimers', 'currentRoom']),
    ...mapGetters('user', ['currentUserId']),
    globalChaseDisabled () {
      const globalChaseTimer = this.chaseTimers.global?.[this.currentRoom.id]
      return !this.signatures ||
        this.signatures.data.length === 0 ||
        (globalChaseTimer && this.currentDate.isBefore(globalChaseTimer))
    },
    globalChaseTooltip () {
      const hasValidatorsToChase = this.signatures?.data?.some(s => !s.validated && (s.status === 'unvalidated' || s === 'sent'))
      const hasSignatoriesToChase = this.signatures?.data?.some(s => s.signatories.some(signatory => signatory.status === 'sent'))
      if (hasValidatorsToChase && hasSignatoriesToChase) {
        return this.$t('project.project-settings.signatures.ProjectSettingsSignatures.globalChaseValidatorsAndSignatories')
      } else if (hasValidatorsToChase) {
        return this.$t('project.project-settings.signatures.ProjectSettingsSignatures.globalChaseValidators')
      } else if (hasSignatoriesToChase) {
        return this.$t('project.project-settings.signatures.ProjectSettingsSignatures.globalChaseSignatories')
      }
      return null
    },
    customKeySort () {
      return {
        'documents': (a, b) => numericSort(a, b),
        'date': (a, b) => dayjs(a).isBefore(dayjs(b)) ? -1 : 1,
        'status': (a, b) => numericSort(this.$t(`common.envelopeStatus.${a}`), this.$t(`common.envelopeStatus.${b}`)),
        'validators': (a, b) => numericSort(a[0]?.validator.fullName, b[0]?.validator.fullName),
        'signatories': (a, b) => numericSort(a[0]?.fullName, b[0]?.fullName),
      }
    },
  },
  created () {
    this.prepareGetSignatures()
    this.currentDateInterval()
  },
  beforeUnmount () {
    clearInterval(this.currentDateIntervalId)
  },
  methods: {
    ...mapActions('room', [GET_SIGNATURES, CHASE_USERS, CHASE_VALIDATOR]),
    onExpandClick (item) {
      if (this.expanded.includes(item.id)) {
        this.expanded = this.expanded.filter(id => id !== item.id)
      } else {
        this.expanded.push(item.id)
      }
    },
    async prepareGetSignatures () {
      try {
        await this.GET_SIGNATURES(this.mnemo)
        if (this.signatures.data.length > 0) {
          this.expanded.push(this.signatures.data[0])
        }
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.project-settings.signatures.ProjectSettingsSignatures.getSignaturesError'))
      }
    },
    envelopeStatusColor (item) {
      switch (item.status) {
        case 'draft':
        case 'temp':
          return '#e0e0e0'
        case 'sent':
          return 'warning'
        case 'signed':
          return 'success'
        case 'voided':
          return 'error'
        case 'unvalidated':
          return 'warning'
      }
      return null
    },
    getDate (date) {
      return ISOToShortDate(date)
    },
    envelopeCanBeChase (envelope) {
      return envelope.signatories.some(signatory => signatory.status === 'sent') ||
        envelope.status === 'unvalidated'
    },
    canExpand (envelope) {
      return envelope.signatories.length > 0 || envelope.validations.length > 0
    },
    showExpand (envelope) {
      return envelope.signatories.length > 1 || envelope.validations.length > 1
    },
    canGlobalChase () {
      return this.signatures?.data.some(envelope => {
        return this.envelopeCanBeChase(envelope)
      })
    },
    async prepareGlobalChase () {
      // TODO : Handle pendingIDS with vuex-action
      this.globalChaseLoading = true
      try {
        for (const envelope of this.signatures.data) {
          if (!envelope.validated && (envelope.status === 'unvalidated' || envelope.status === 'sent')) {
            await this.CHASE_VALIDATOR({
              mnemo: this.mnemo,
              envelopeId: envelope.id,
              data: [],
            })
          }
          if (envelope.status === 'sent') {
            await this.CHASE_USERS({
              mnemo: this.mnemo,
              envelopeId: envelope.id,
            })
          }
        }
        this.$store.commit(`room/${ADD_CHASE_TIMER_GLOBAL}`, this.currentRoom.id)
        toast.success(this.$t('common.chaseSuccess'))
      } catch (error) {
        console.error(error)
        toast.error(this.$t('common.chaseError'))
      } finally {
        this.globalChaseLoading = false
      }
    },
    envelopeResourceLink (envelopeId) {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/envelope/${envelopeId}`
    },
    currentDateInterval () {
      this.currentDateIntervalId = setInterval(() => { this.currentDate = dayjs() }, 1000 * 60)
    },
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";

.ProjectSettingsSignatures-loader {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(#fff, .8);
  z-index: 1;
}
:deep(.ProjectSettingsSignatures-table) {
  background-color: transparent;
  table {
    border-collapse: separate;
  }
  th {
    color: rgb(var(--v-theme-accent)) !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;

    &:first-child {
      max-width: 20px;
      padding: 0;
    }
  }
  td {
    color: rgb(var(--v-theme-accent)) !important;
    font-size: 14px !important;

    &:first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    &:last-child {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
  tbody {
    tr {
      cursor: pointer;
      @media #{map-get($display-breakpoints, 'xs')} {
        margin-bottom: 10px;
      }

      td {
        border-bottom: none!important;
      }

      td:first-child {
        max-width: 20px;
        padding: 0;

      }
    }

    tr:not(:first-of-type) {
      td {
        border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
      }
    }

    tr.expanded-row {
      td {
        padding-top: 12px;
        border-top: none !important;
      }
    }
  }
}
.ProjectSettingsSignatures-expandIcon {
  transition-duration: 400ms;
  transition-property: transform;
}
.ProjectSettingsSignatures-expandIcon--open {
  transform: rotate(90deg);
}
</style>
